'use client';

import {
  type ComponentProps,
  createContext,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '../../lib/utils';
import { msg } from '../../localization/i18n';
import type { ComboboxOption } from '../TovCombobox/TovCombobox';
import { TovButton } from '../TovButton/TovButton';
import { TovIcon } from '../TovIcon/TovIcon';
import { XIcon } from 'lucide-react';
import { Badge } from '../TovBadge/TovBadge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../TovCommand/TovCommand';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../TovPopover/TovPopover';

export interface TagsFieldProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  value: string[];
  options: ComboboxOption[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  label?: string;
}

export function TagsField({
  className,
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  label,
  ...props
}: TagsFieldProps) {
  const [open, setOpen] = useState(false);
  const selectedValues = useMemo(() => new Set(value), [value]);
  const selectedOptions = useMemo(
    () => options.filter((option) => selectedValues.has(option.value)),
    [options, selectedValues],
  );

  const toggleValue = (nextValue: string) => {
    if (selectedValues.has(nextValue)) {
      onChange(value.filter((item) => item !== nextValue));
      return;
    }

    onChange([...value, nextValue]);
  };

  return (
    <div className={cn('tov-tags-field', className)} {...props}>
      <Tags className="tov:w-full" onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <TovButton
            aria-label={label}
            className={cn(
              'tov-tags-field__trigger tov:h-auto tov:w-full tov:justify-between tov:p-2 tov:disabled:cursor-not-allowed tov:disabled:bg-muted tov:disabled:text-muted-foreground tov:disabled:opacity-100',
              open && 'tov-tags-field__trigger--open',
            )}
            disabled={disabled}
            role="combobox"
            variant="outline"
          >
            <div className="tov-tags-field__values tov:flex tov:flex-wrap tov:items-center tov:gap-1">
              {selectedOptions.length > 0 ? (
                selectedOptions.map((option) => (
                  <TagsValue
                    className="tov-tags-field__pill"
                    key={option.value}
                    onRemove={() => toggleValue(option.value)}
                  >
                    {option.label}
                  </TagsValue>
                ))
              ) : (
                <span className="tov-tags-field__placeholder">
                  {placeholder ??
                    msg(
                      'components.tags_field.components_tags_field_select_values',
                    )}
                </span>
              )}
            </div>
            <span className="tov-tags-field__trigger-icon" aria-hidden="true">
              <TovIcon name="caret-down" />
            </span>
          </TovButton>
        </PopoverTrigger>

        <TagsContent className="tov-tags-field__panel">
          <TagsInput
            aria-label={msg('general.fields.search.label')}
            className="tov-tags-field__search"
            placeholder={
              searchPlaceholder ??
              msg('components.tags_field.components_tags_field_search_values')
            }
          />
          <TagsList className="tov-tags-field__options">
            <TagsEmpty>
              {emptyMessage ??
                msg(
                  'components.tags_field.components_tags_field_no_matching_values',
                )}
            </TagsEmpty>
            <TagsGroup>
              {options.map((option) => {
                const selected = selectedValues.has(option.value);

                return (
                  <TagsItem
                    className={cn(
                      'tov-tags-field__option',
                      selected && 'tov-tags-field__option--selected',
                    )}
                    disabled={option.disabled}
                    key={option.value}
                    keywords={option.keywords}
                    onSelect={() => toggleValue(option.value)}
                    value={option.value}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'tov-tags-field__option-indicator',
                        selected &&
                          'tov-tags-field__option-indicator--selected',
                      )}
                    >
                      {selected ? <TovIcon name="check" /> : null}
                    </span>
                    <span className="tov-tags-field__option-copy">
                      <span className="tov-tags-field__option-label">
                        {option.label}
                      </span>
                      {option.description ? (
                        <span className="tov-tags-field__option-description">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                  </TagsItem>
                );
              })}
            </TagsGroup>
          </TagsList>
        </TagsContent>
      </Tags>
    </div>
  );
}

type TagsContextType = {
  value?: string;
  setValue?: (value: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width?: number;
  setWidth?: (width: number) => void;
};

const TagsContext = createContext<TagsContextType>({
  value: undefined,
  setValue: undefined,
  open: false,
  onOpenChange: () => {},
  width: undefined,
  setWidth: undefined,
});

const useTagsContext = () => {
  const context = useContext(TagsContext);

  if (!context) {
    throw new Error('useTagsContext must be used within a TagsProvider');
  }

  return context;
};

type TagsProps = {
  value?: string;
  setValue?: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
  className?: string;
};

const Tags = ({
  value,
  setValue,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  children,
  className,
}: TagsProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [width, setWidth] = useState<number>();
  const ref = useRef<HTMLDivElement>(null);

  const open = controlledOpen ?? uncontrolledOpen;
  const onOpenChange = controlledOnOpenChange ?? setUncontrolledOpen;

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });

    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <TagsContext.Provider
      value={{ value, setValue, open, onOpenChange, width, setWidth }}
    >
      <Popover onOpenChange={onOpenChange} open={open}>
        <div className={cn('tov:relative tov:w-full', className)} ref={ref}>
          {children}
        </div>
      </Popover>
    </TagsContext.Provider>
  );
};

type TagsValueProps = ComponentProps<typeof Badge>;

const TagsValue = ({
  className,
  children,
  onRemove,
  ...props
}: TagsValueProps & { onRemove?: () => void }) => {
  const handleRemoveMouseDown: MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRemoveClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove?.();
  };

  return (
    <Badge
      className={cn(
        'tov-tags-value tov:flex tov:items-center tov:gap-2',
        className,
      )}
      {...props}
    >
      <span className="tov-tags-value__label">{children}</span>
      {onRemove && (
        <TovButton
          aria-label={msg('general.actions.remove')}
          className="tov-tags-value__remove"
          onClick={handleRemoveClick}
          onMouseDown={handleRemoveMouseDown}
        >
          <XIcon size={12} />
        </TovButton>
      )}
    </Badge>
  );
};

type TagsContentProps = ComponentProps<typeof PopoverContent>;

const TagsContent = ({ className, children, ...props }: TagsContentProps) => {
  const { width } = useTagsContext();

  return (
    <PopoverContent
      className={cn('tov:p-0', className)}
      style={{ width }}
      {...props}
    >
      <Command>{children}</Command>
    </PopoverContent>
  );
};

type TagsInputProps = ComponentProps<typeof CommandInput>;

const TagsInput = ({ className, ...props }: TagsInputProps) => (
  <CommandInput className={cn('tov:h-9', className)} {...props} />
);

type TagsListProps = ComponentProps<typeof CommandList>;

const TagsList = ({ className, ...props }: TagsListProps) => (
  <CommandList className={cn('tov:min-h-0', className)} {...props} />
);

type TagsEmptyProps = ComponentProps<typeof CommandEmpty>;

const TagsEmpty = ({ children, className, ...props }: TagsEmptyProps) => (
  <CommandEmpty className={className} {...props}>
    {children ?? 'No tags found.'}
  </CommandEmpty>
);

const TagsGroup = CommandGroup;

type TagsItemProps = ComponentProps<typeof CommandItem>;

const TagsItem = ({ className, ...props }: TagsItemProps) => (
  <CommandItem
    className={cn(
      'tov:cursor-pointer tov:items-center tov:justify-between',
      className,
    )}
    {...props}
  />
);
